import React, { useState, useCallback, useEffect, useMemo, Fragment, forwardRef } from 'react'
import PropTypes from 'prop-types'
import styled, { css, keyframes } from 'styled-components'
import { color } from 'styled-system'

import { Column, Row } from '../Grid'
import { Text } from '../Text'
import { Chevron, PaperPlane } from '../Icons'

import { useOuterClickNotifier, useHotkey } from '../../../hooks'

const Select = forwardRef(
  (
    {
      register,
      name,
      onChange,
      value,
      label,
      placeholder,
      options,
      error,
      disabled,
      icon,
      variant,
      clearButton,
      selectTextMarginTop = '12px',
      placeholderFontSize = 'small',
      chevronColor,
      title,
      ...props
    },
    selectContainer
  ) => {
    useOuterClickNotifier(() => isOpen && setIsOpen(false), selectContainer)

    const [isOpen, setIsOpen] = useState(false)
    const currentOption = useMemo(() => {
      const flattenedOptions = options?.reduce((prev, curr) => {
        if (curr.options) {
          return prev.concat(curr.options)
        }
        return prev.concat(curr)
      }, [])

      return flattenedOptions?.find(op => op.value === value || op === value)
    }, [value, options])

    const hasOptionSelected = Boolean(currentOption)
    const toggleIsOpen = () => setIsOpen(previousIsOpen => !previousIsOpen)
    useHotkey('Escape', () => isOpen && setIsOpen(false))

    const pickOption = useCallback(
      op => {
        setIsOpen(false)
        onChange({ target: { value: op.value || op, name } })
      },
      [name]
    )

    const renderOptions = (options, hasGroup = false) => {
      return options.map((option, index) => {
        if (option?.options) {
          return (
            <Fragment key={`${option.groupLabel}-${index}`}>
              <StyledOptionGroup as='li'>
                <Row>{option.groupLabel}</Row>
              </StyledOptionGroup>

              {renderOptions(option.options, true)}
            </Fragment>
          )
        }

        return (
          <StyledOption
            key={`${option.value || value}-${index}`}
            selected={option === value}
            onClick={() => pickOption(option)}
            as='li'
            ml={hasGroup ? 12 : 0}
          >
            <Row>
              {option.label || option}
              {icon && (
                <Row display='none' ml='9px' alignItems='center'>
                  <PaperPlane />
                </Row>
              )}
            </Row>
          </StyledOption>
        )
      })
    }

    useEffect(() => {
      register && register({ name })
    }, [register])

    const clearValue = () => {
      onChange({ target: { value: '' } })
    }

    return (
      <Fragment>
        {variant === 'primary' && (
          <Container width='100%' alignItems='flex-start' {...props}>
            {title && (
              <Text fontSize='14px' fontWeight='400' lineHeight='20px' color='#2E3035' mb='4px'>
                {title}
              </Text>
            )}

            <SelectBox
              data-testid='select-input-primary'
              variant={variant}
              type='button'
              disabled={disabled}
              onClick={toggleIsOpen}
              error={error}
              ref={selectContainer}
              bg='white'
            >
              {icon}
              <Row
                width='100%'
                p={icon && '12px 0px 12px 12px'}
                mt={selectTextMarginTop}
                justifyContent='space-between'
                alignItems='center'
              >
                <Row>
                  <Text fontSize='10px' position='absolute' top='2px' color='primary' as='label'>
                    {label}
                  </Text>
                  <Text
                    as='span'
                    variant={hasOptionSelected ? 'tiny' : 'small'}
                    position='absolute'
                    top='20px'
                    color={hasOptionSelected ? 'darkPrimary' : 'black'}
                  >
                    {currentOption?.label || currentOption}
                  </Text>
                  <Text
                    as='span'
                    display={!hasOptionSelected ? 'auto' : 'none'}
                    variant={placeholderFontSize}
                    color='gray.n850'
                    mt='6px'
                  >
                    {placeholder}
                  </Text>
                </Row>
                {!disabled && (
                  <Chevron
                    size={24}
                    color={chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'}
                    variant={!isOpen ? 'down' : 'top'}
                  />
                )}
              </Row>
            </SelectBox>
            {error && (
              <Text textAlign='left' color='red' fontSize={['9px', '11px']} mt='6px'>
                {error}
              </Text>
            )}
            {isOpen && (
              <OptionsContainer error={error}>
                <OptionList>
                  {clearButton && (
                    <ClearOption onClick={clearValue} as='li'>
                      <Row>Limpar selecionado(a)</Row>
                    </ClearOption>
                  )}
                  {renderOptions(options)}
                </OptionList>
              </OptionsContainer>
            )}
          </Container>
        )}
        {variant === 'secondary' && (
          <Container width='100%' alignItems='flex-start' {...props}>
            {title && (
              <Text fontSize='14px' fontWeight='400' lineHeight='20px' color='#2E3035' mb='4px'>
                {title}
              </Text>
            )}

            <SelectBox
              data-testid='select-input-secondary'
              variant={variant}
              type='button'
              disabled={disabled}
              onClick={toggleIsOpen}
              error={error}
              ref={selectContainer}
              bg='#F7F9FB'
            >
              {icon}
              <Row width='100%' p={icon && '12px 0px 12px 12px'} justifyContent='space-between'>
                <Row>
                  {hasOptionSelected && variant !== 'primary' && (
                    <Text fontSize='10px' position='absolute' top={0} color='gray.n400' as='label'>
                      {variant === 'secondary' ? '' : label}
                    </Text>
                  )}

                  <Text
                    as='span'
                    variant='small'
                    position='absolute'
                    // top={label && variant === 'secondary' ? '12px' : '20px'}
                    color='black'
                  >
                    {currentOption?.label || currentOption}
                  </Text>

                  <Text
                    as='span'
                    display={!hasOptionSelected ? 'auto' : 'none'}
                    variant='small'
                    color='gray.n400'
                  >
                    {placeholder}
                  </Text>
                </Row>
                {!disabled && (
                  <Chevron
                    size={20}
                    color={chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'}
                    variant={!isOpen ? 'down' : 'top'}
                  />
                )}
              </Row>
            </SelectBox>
            {isOpen && (
              <OptionsContainer data-testid='select-secondary-primary' error={error}>
                <OptionList>
                  {clearButton && (
                    <ClearOption onClick={clearValue} as='li'>
                      <Row>Limpar selecionado(a)</Row>
                    </ClearOption>
                  )}
                  {renderOptions(options)}
                </OptionList>
              </OptionsContainer>
            )}
          </Container>
        )}
        {variant === 'borderless' && (
          <Container width='100%' ref={selectContainer} alignItems='flex-start' {...props}>
            {title && (
              <Text fontSize='14px' fontWeight='400' lineHeight='20px' color='#2E3035' mb='4px'>
                {title}
              </Text>
            )}

            <SelectBox
              data-testid='select-input-borderless'
              variant={variant}
              type='button'
              disabled={disabled}
              onClick={toggleIsOpen}
              error={error}
              ref={selectContainer}
              bg='white'
            >
              {icon}
              <Row width='100%' justifyContent='space-between' alignItems='center'>
                <Text as='span' variant='regular'>
                  {currentOption?.label || currentOption}
                </Text>

                <Text
                  as='span'
                  display={!hasOptionSelected ? 'auto' : 'none'}
                  variant='small'
                  color='gray.n400'
                >
                  {placeholder}
                </Text>

                {!disabled && (
                  <Chevron
                    size={20}
                    color={chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'}
                    variant={!isOpen ? 'down' : 'top'}
                  />
                )}
              </Row>
            </SelectBox>
            {isOpen && (
              <OptionsContainer data-testid='select-value-borderless' error={error}>
                <OptionList>
                  {clearButton && (
                    <ClearOption onClick={clearValue} as='li'>
                      <Row>Limpar selecionado(a)</Row>
                    </ClearOption>
                  )}
                  {renderOptions(options)}
                </OptionList>
              </OptionsContainer>
            )}
          </Container>
        )}

        {variant === 'tertiary' && (
          <Container width='100%' alignItems='flex-start' {...props}>
            {title && (
              <Text fontSize='14px' fontWeight='400' lineHeight='20px' color='#2E3035' mb='4px'>
                {title}
              </Text>
            )}

            <SelectBox
              data-testid='select-input-tertiary'
              type='button'
              disabled={disabled}
              onClick={toggleIsOpen}
              error={error}
              bg='white'
            >
              {icon}
              <Row
                width='100%'
                p={icon && '12px 0px 12px 12px'}
                mt={12}
                justifyContent='space-between'
              >
                <Row>
                  {hasOptionSelected && (
                    <Text fontSize='10px' position='absolute' top='2px' color='primary' as='label'>
                      {label}
                    </Text>
                  )}
                  <Text as='span' variant='small' position='absolute' top={'20px'} color='black'>
                    {currentOption?.label || currentOption}
                  </Text>
                  <Text
                    as='span'
                    display={!hasOptionSelected ? 'auto' : 'none'}
                    variant={'regular'}
                    color='black'
                  >
                    {placeholder}
                  </Text>
                </Row>
                {!disabled && (
                  <Chevron
                    size={20}
                    color={chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'}
                    variant={!isOpen ? 'down' : 'top'}
                  />
                )}
              </Row>
            </SelectBox>
            {isOpen && (
              <OptionsContainer
                data-testid='select-value-tertiary'
                ref={selectContainer}
                error={error}
              >
                <OptionList>
                  {clearButton && (
                    <ClearOption onClick={clearValue} as='li'>
                      <Row>Limpar selecionado(a)</Row>
                    </ClearOption>
                  )}
                  {renderOptions(options)}
                </OptionList>
              </OptionsContainer>
            )}
          </Container>
        )}
      </Fragment>
    )
  }
)

const SelectBox = styled.button(
  ({ error, disabled, variant, theme: { colors } }) => css`
    ${color}

    text-align: left;
    display: flex;
    align-items: center;
    padding-left: 12px;
    width: 100%;
    height: 48px;
    position: relative;
    cursor: ${!disabled ? 'pointer' : 'not-allowed'};
    transition: all 0.3s ease;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 24px;
    overflow: hidden;

    ${variant === 'borderless'
      ? css`
          border: none;
          color: ${colors.primary};
          font-weight: bold;
          &:focus {
            outline: none;
            border: none;
          }
        `
      : css`
          color: ${disabled ? colors.gray.n300 : '#2E3035'};
          border-radius: 4px;
          border: 0.5px solid #e7eaf4;

          &:focus {
            outline: none;
            border: 0.5px solid black;
          }
        `}
  `
)

const OptionsContainer = styled.div(
  ({ error, theme: { colors } }) => css`
    z-index: 25;
    max-height: 259px;
    position: absolute;
    width: 100%;
    background: ${colors.white};
    border: 1px solid #e7eaf4;
    border-radius: 4px;
    overflow: auto;
    top: ${error ? 'calc(100% - 18px)' : '52px'};

    ::-webkit-scrollbar {
      width: 3px;
      background-color: ${colors.gray.n200};
    }
    ::-webkit-scrollbar-thumb {
      background-color: #babac0;
      border-radius: 16px;
    }
    ::-webkit-scrollbar-button {
      display: none;
    }
  `
)

const isOpenMenu = keyframes`
  from {
    max-height: 0px;
  }
  to {
    max-height: 200px;
  }
`

const OptionList = styled.ul`
  animation: ${isOpenMenu} 0.3s ease;
  background-color: #fff;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
`

const Container = styled(Column)`
  position: relative;
`

const StyledOption = styled(Text)(
  ({ theme: { colors } }) => css`
    text-align: left;
    list-style-type: none;
    padding: 8px 16px;
    cursor: pointer;
    font-size: 14px;
    &:hover {
      color: ${colors.primary};
    }
    &:focus {
      outline: none;
    }
  `
)

const ClearOption = styled(StyledOption)(
  ({ theme: { colors } }) => css`
    border-bottom: 1px solid ${colors.gray.n300};
  `
)

const StyledOptionGroup = styled(Text)(
  ({ theme: { colors } }) => css`
    text-align: left;
    list-style-type: none;
    padding: 8px 16px;
    cursor: default;
    font-size: 14px;
    line-height: 24px;
    color: ${colors.gray.n750};
  `
)

Select.defaultProps = {
  option: [],
  disabled: false,
  variant: 'primary',
  clearButton: false
}

Select.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired
      }),
      PropTypes.shape({
        groupLabel: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired
          })
        )
      })
    ])
  ).isRequired,
  register: PropTypes.func,
  error: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  clearButton: PropTypes.bool
}

export { Select }
